import {
  type CheckboxChangeEvent,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Spin,
} from "antd"
import dayjs, { type Dayjs } from "dayjs"
import { useCallback, useEffect, useMemo, useRef } from "react"

import Flex from "#blocks/Flex"
import CheckboxWrapper from "#blocks/Form/CheckboxWrapper"
import { SFormHeader } from "#blocks/Form/formComponents"
import SessionSelect from "#blocks/Form/SessionSelect"
import TagWithTooltip from "#blocks/TagWithTooltip"
import TextOverrun from "#blocks/TextOverrun"
import ClickableStudentInfoRow from "#components/ClickableStudentInfoRow"
import { MAKEUP_REASON_OPTIONS } from "#constants/makeupOptions"
import { type AddMakeupFormValues } from "#customTypes/composedTypes/makeupTypes"
import { StudentSkillType } from "#customTypes/composedTypes/studentTypes"
import { type Uuid } from "#customTypes/globalTypes"
import {
  coalesceMakeupFilterDate,
  resolveMakeupFilterDate,
} from "#forms/AddMakeup/makeupSessionWindow"
import useMakeupFormData from "#forms/AddMakeup/useMakeupFormData"
import { useGetAllStudents } from "#hooks/jotai/useGetStudents"
import useOptimizedFilter from "#hooks/newHooks/useOptimizedFilter"
import { useErrorModal } from "#hooks/useErrorModal"
import { getNow } from "#utils/dateHelpers"
import { getCurrentSemester } from "#utils/semesterHelpers"
import { getStudentOptions } from "#utils/studentHelpers"
import {
  formatSkillLevelWithPending,
  getAllSkillLevelsWithPending,
} from "#utils/studentSkillHelpers"

const AddMakeupForm = ({
  initialValues,
}: {
  initialValues: AddMakeupFormValues | undefined
}) => {
  const form = Form.useFormInstance<AddMakeupFormValues>()

  const missedClassId: Uuid | undefined =
    Form.useWatch("missedClassId", form) ?? initialValues?.missedClassId
  const missedSessionId: Uuid | undefined =
    Form.useWatch("missedSessionId", form) ?? initialValues?.missedSessionId
  const makeupFilterDate = coalesceMakeupFilterDate(
    Form.useWatch("makeupFilterDate", form),
    initialValues?.makeupFilterDate
  )
  const isMakeupScheduled: boolean =
    Form.useWatch("isMakeupScheduled", form) ?? initialValues?.isMakeupScheduled

  const isEdit = initialValues?.id != null

  const {
    student,
    studentClasses,
    missedClass,
    missedSession,
    missedProject,
    initialMakeupSession,
    missedSessionOptions,
    makeupSessionOptions,
    isReady,
    errors,
  } = useMakeupFormData(initialValues)

  const openErrorModal = useErrorModal("viewing AddMakeupForm")

  useEffect(() => {
    if (errors) {
      const context = `
AddMakeupForm
`
      openErrorModal(errors, context)
    }
  }, [errors, openErrorModal])

  // Changing student should rebuild missed class, missed session, and makeup class and session options
  // and then pick the first available option
  const onSelectStudent = () => {
    form.setFieldValue("missedClassId", null)
    form.setFieldValue("missedSessionId", null)
    form.setFieldValue("makeupClassId", null)
    form.setFieldValue("makeupSessionId", null)
  }

  const onSelectClassToMiss = () => {
    form.setFieldValue("missedSessionId", null)
    form.setFieldValue("makeupClassId", null)
    form.setFieldValue("makeupSessionId", null)
  }

  // True once the user clears the picker during this visit. The sync effect
  // must not put a date back before save; the next open fills today because
  // the stored value is null.
  const filterDateCleared = useRef(false)

  const getMakeupFilterDateForSession = useCallback(() => {
    return resolveMakeupFilterDate({
      makeupSessionStart: initialMakeupSession?.eventStart,
      missedSessionStart: missedSession?.eventStart,
      missedClassSemester: missedClass?.semester,
      currentSemester: getCurrentSemester(),
      reopenWithoutSavedFilterDate:
        initialValues?.id != null && initialValues.makeupFilterDate == null,
      now: getNow(),
    })
  }, [
    initialMakeupSession?.eventStart,
    initialValues?.id,
    initialValues?.makeupFilterDate,
    missedClass?.semester,
    missedSession?.eventStart,
  ])

  const onToggleMakeupScheduled = (e: CheckboxChangeEvent) => {
    const isChecked = e.target.checked
    filterDateCleared.current = false
    form.setFieldValue(
      "makeupFilterDate",
      isChecked ? getMakeupFilterDateForSession() : null
    )
    if (!isChecked) {
      form.setFieldValue("makeupClassId", null)
      form.setFieldValue("makeupSessionId", null)
    }
  }

  useEffect(() => {
    if (!isMakeupScheduled || !missedSession?.eventStart) return
    if (filterDateCleared.current) return
    const nextDate = getMakeupFilterDateForSession()
    const current = form.getFieldValue("makeupFilterDate") as unknown
    // Filling an empty date (planned absence reopened, or a cleared date
    // saved last time) must not drop the booked session. Clearing only
    // happens when a date was already set and is being replaced.
    if (!dayjs.isDayjs(current) || !current.isValid()) {
      form.setFieldValue("makeupFilterDate", nextDate)
      return
    }
    if (current.isSame(nextDate, "day")) return
    form.setFieldValue("makeupFilterDate", nextDate)
    form.setFieldValue("makeupClassId", null)
    form.setFieldValue("makeupSessionId", null)
  }, [
    form,
    isMakeupScheduled,
    missedSession?.eventStart,
    getMakeupFilterDateForSession,
  ])

  const onSelectMakeupClassAndSession = (value: string) => {
    const [makeupClassId, makeupSessionId] = value.split(" ")
    form.setFieldValue("makeupClassId", makeupClassId)
    form.setFieldValue("makeupSessionId", makeupSessionId)
  }

  const onChangeMakeupFilterDate = (value: Dayjs | null) => {
    filterDateCleared.current = value == null
    form.setFieldValue("makeupClassId", null)
    form.setFieldValue("makeupSessionId", null)
  }

  const semester = studentClasses.find(
    (studentClass) => studentClass.id === missedClassId
  )?.semester

  useEffect(() => {
    form.setFieldsValue({ semester })
  }, [semester, form])

  // Levels as of the missed class's semester, each with the promotion an
  // approved eval has already recorded (see studentSkillHelpers).
  const currentLevels = getAllSkillLevelsWithPending(student, semester)

  const missedClassOptions = useMemo(
    () =>
      studentClasses
        ?.sort((a, b) => dayjs(b.startDate).diff(dayjs(a.startDate))) // Newest first
        ?.map(({ id, name }) => ({
          value: id,
          label: name,
        })),
    [studentClasses]
  )

  // Select first MISSED class and session if there's none selected
  useEffect(() => {
    const formMissedClassId = form.getFieldValue("missedClassId")
    const formMissedSessionId = form.getFieldValue("missedSessionId")

    if (!formMissedClassId && !!missedClassOptions?.length) {
      form.setFieldValue("missedClassId", missedClassOptions[0].value)
    }

    const firstEnabledMissedSession = missedSessionOptions?.find(
      (opt) => !opt.disabled
    )
    if (!formMissedSessionId && firstEnabledMissedSession) {
      form.setFieldValue("missedSessionId", firstEnabledMissedSession.value)
    }
  }, [
    form,
    missedClassOptions,
    missedSessionOptions,
    missedClassId,
    missedSessionId,
  ])

  // Select first MAKEUP class and session on makeupFilterDate change
  useEffect(() => {
    // Do NOT include initialValues in this check.
    const existingMakeupClassId = form.getFieldValue("makeupClassId")
    const existingMakeupSessionId = form.getFieldValue("makeupSessionId")

    const isOptionCurrent = makeupSessionOptions.some(
      (option) =>
        option.value === `${existingMakeupClassId} ${existingMakeupSessionId}`
    )

    if (
      (isEdit && existingMakeupSessionId && isOptionCurrent) ||
      !isMakeupScheduled
    )
      return

    // Warned options (full, in the past) stay selectable but should not be
    // the silent default.
    const firstEnabledMakeup =
      makeupSessionOptions.find((opt) => !opt.disabled && !opt.hasWarning) ??
      makeupSessionOptions.find((opt) => !opt.disabled)
    if (!firstEnabledMakeup?.value) return

    const [makeupClassId, makeupSessionId] = (
      firstEnabledMakeup.value as string
    ).split(" ")
    form.setFieldValue("makeupClassId", makeupClassId)
    form.setFieldValue(
      "makeupSessionId",
      makeupSessionId ?? firstEnabledMakeup.value
    )
  }, [form, makeupSessionOptions, makeupFilterDate, isEdit, isMakeupScheduled])

  const allStudents = useGetAllStudents()
  const studentOptions = useMemo(() => {
    return getStudentOptions(allStudents)
  }, [allStudents])

  const { filteredItems: filteredStudentOptions, onSearch: onStudentSearch } =
    useOptimizedFilter(studentOptions)

  return (
    <>
      <Row gutter={40}>
        <Col span={24}>
          <Flex>
            <SFormHeader>Makeup Info</SFormHeader>
          </Flex>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="studentId"
                label="Student"
                rules={[
                  {
                    required: !isEdit,
                    message: "Please select a student!",
                  },
                ]}
              >
                {isEdit && student ? (
                  <Flex>
                    <ClickableStudentInfoRow student={student} border />
                  </Flex>
                ) : (
                  <Select
                    loading={!isReady}
                    showSearch
                    filterOption={() => true}
                    onSearch={onStudentSearch}
                    options={filteredStudentOptions}
                    onChange={onSelectStudent}
                    placeholder="Select a student"
                  />
                )}
              </Form.Item>
            </Col>
            {student && (
              <>
                <Col span={3}>
                  <Form.Item label="Core skill level">
                    {formatSkillLevelWithPending(
                      currentLevels[StudentSkillType.core]
                    )}
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item label="Digital skill level">
                    {formatSkillLevelWithPending(
                      currentLevels[StudentSkillType.digital]
                    )}
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item label="Personal phone">
                    <TextOverrun text={student?.personalPhone} />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item label="Personal email">
                    <TextOverrun text={student?.personalEmail} />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
          <Row gutter={16}>
            <Col span={3}>
              <Form.Item label="Allergies">
                <Flex wrap gap={4}>
                  {student?.allergies
                    ? student?.allergies?.map((allergyKey) => (
                        <TagWithTooltip key={allergyKey} tagName={allergyKey} />
                      ))
                    : "None"}
                </Flex>
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="Tags">
                <Flex wrap gap={4}>
                  {student?.tags
                    ? student?.tags?.map((tagKey) => (
                        <TagWithTooltip key={tagKey} tagName={tagKey} />
                      ))
                    : "None"}
                </Flex>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Notes">
                <TextOverrun text={student?.notes ?? ""} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                label="Reason for absence"
                name="reason"
                rules={[{ required: true, message: "Please select a reason!" }]}
              >
                <Select
                  options={MAKEUP_REASON_OPTIONS}
                  placeholder="Select a reason for absence"
                />
              </Form.Item>
            </Col>
          </Row>

          <SFormHeader>Class to Miss</SFormHeader>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="missedClassId"
                label="Class to Miss"
                rules={[
                  { required: true, message: "Please select a class to miss!" },
                ]}
              >
                <Select
                  options={missedClassOptions}
                  onSelect={onSelectClassToMiss}
                  placeholder="Select a class to miss"
                />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item
                name="missedSessionId"
                label="Session Date"
                rules={[
                  { required: true, message: "Please select a session date!" },
                ]}
              >
                {!missedSessionOptions.length && !isReady ? (
                  <Flex gap={8} style={{ alignItems: "center", height: 32 }}>
                    <Spin size="small" />
                    <span style={{ color: "#999" }}>Loading sessions…</span>
                  </Flex>
                ) : (
                  <SessionSelect
                    options={missedSessionOptions}
                    placeholder="Select a session date"
                  />
                )}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Project" hidden={!missedProject}>
                {missedProject?.name}
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={8}>
              <CheckboxWrapper
                name="isMakeupScheduled"
                label="Schedule makeup"
                value={isMakeupScheduled}
                onChange={onToggleMakeupScheduled}
              />
            </Col>
          </Row>

          {isMakeupScheduled && (
            <Flex gap={10} col>
              <SFormHeader>Makeup Class</SFormHeader>

              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item
                    label="Find sessions after (up to 2 weeks)"
                    name="makeupFilterDate"
                  >
                    <DatePicker
                      format="M/D/YY - dddd"
                      style={{ width: "100%" }}
                      minDate={dayjs("2016-01-01")}
                      onChange={onChangeMakeupFilterDate}
                    />
                  </Form.Item>
                </Col>
                <Col
                  span={8}
                  style={{ height: "100%", paddingTop: 26, paddingLeft: 30 }}
                >
                  <CheckboxWrapper
                    name="isScheduledForNextSemester"
                    label="Schedule for next semester"
                    // value={isScheduledForNextSemester}
                    // onChange={onToggleIsScheduledForNextSemester}
                  />
                </Col>
              </Row>
              <Row>
                <Col span={16}>
                  <Form.Item
                    name="makeupClassAndSessionId"
                    label="Makeup Session"
                    preserve={false}
                    rules={[
                      {
                        required: true,
                        message: "Please select a makeup class!",
                      },
                    ]}
                  >
                    {!makeupSessionOptions.length && !isReady ? (
                      <Flex
                        gap={8}
                        style={{ alignItems: "center", height: 32 }}
                      >
                        <Spin size="small" />
                        <span style={{ color: "#999" }}>Loading sessions…</span>
                      </Flex>
                    ) : (
                      <SessionSelect
                        onSelect={onSelectMakeupClassAndSession}
                        options={makeupSessionOptions}
                        notFoundContent="No sessions found — try changing the filter date"
                        placeholder="Select a makeup session"
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Flex>
          )}
        </Col>
      </Row>

      <Form.Item name="id" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="semester" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="makeupClassId" hidden preserve={false}>
        <Input />
      </Form.Item>
      <Form.Item name="makeupSessionId" hidden preserve={false}>
        <Input />
      </Form.Item>
    </>
  )
}

export default AddMakeupForm
